<template>
  <!-- Только тур работы: у онбординга прогресс и выход теперь в самом окне
       тура (ТЕСТ ОНБОРДИНГ §0) — кольцо/панель здесь ему больше не нужны. -->
  <div
    v-if="guidanceStore.activeKind === 'work' && guidanceStore.isActive && !guidanceStore.isModalStep"
    ref="rootRef"
    class="guidance-floating-button"
    :class="{ 'guidance-floating-button--expanded': guidanceStore.isFloatingExpanded }"
    :style="positionStyle"
    @pointerdown="onDragStart"
  >
    <button
      v-if="!guidanceStore.isFloatingExpanded"
      class="guidance-floating-button__circle"
      :title="t('guidance.floatingTooltip')"
      @click="onClick"
    >
      <svg
        class="guidance-floating-button__ring"
        viewBox="0 0 36 36"
      >
        <circle
          class="guidance-floating-button__ring-bg"
          cx="18"
          cy="18"
          r="16"
          fill="none"
          stroke-width="3"
        />
        <circle
          class="guidance-floating-button__ring-progress"
          cx="18"
          cy="18"
          r="16"
          fill="none"
          stroke-width="3"
          :stroke-dasharray="circumference"
          :stroke-dashoffset="progressOffset"
          transform="rotate(-90 18 18)"
        />
      </svg>
      <span class="guidance-floating-button__count">
        {{ guidanceStore.progress.step }}/{{ guidanceStore.totalSteps }}
      </span>
    </button>

    <div
      v-else
      class="guidance-floating-button__panel"
    >
      <div class="guidance-floating-button__panel-header">
        <span class="guidance-floating-button__panel-title">{{ t("guidance.floatingTitle") }}</span>
        <button
          class="guidance-floating-button__close"
          @click="collapse"
        >
          <i class="dx-icon-close"></i>
        </button>
      </div>
      <div class="guidance-floating-button__progress-bar">
        <div
          class="guidance-floating-button__progress-fill"
          :style="{ width: progressPercent + '%' }"
        />
      </div>
      <div class="guidance-floating-button__progress-text">
        {{ t("guidance.stepOf", { current: guidanceStore.progress.step, total: guidanceStore.totalSteps }) }}
      </div>
      <div class="guidance-floating-button__actions">
        <button
          class="guidance-floating-button__action-btn guidance-floating-button__action-btn--pause"
          @click="handlePause"
        >
          {{ t("guidance.pauseButton") }}
        </button>
        <button
          class="guidance-floating-button__action-btn guidance-floating-button__action-btn--skip"
          @click="openStopModal"
        >
          {{ t("guidance.skipAllButton") }}
        </button>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGuidanceStore } from "../store/guidance";

const { t } = useI18n();
const guidanceStore = useGuidanceStore();

const POSITION_STORAGE_KEY = "guidance-floating-position-v3";

const rootRef = ref<HTMLElement | null>(null);

const circumference = 2 * Math.PI * 16;

const progressPercent = computed(() => {
  return Math.round(((guidanceStore.progress.step - 1) / guidanceStore.totalSteps) * 100);
});

const progressOffset = computed(() => {
  const progress = (guidanceStore.progress.step - 1) / guidanceStore.totalSteps;
  return circumference * (1 - progress);
});

function handlePause() {
  guidanceStore.pause();
}

function openStopModal() {
  guidanceStore.stopModalAction = "skipAll";
  guidanceStore.stopModalVisible = true;
}

const position = ref<{ right: number; bottom: number } | null>(null);
let dragging = false;
let hasDragged = false;
let startX = 0;
let startY = 0;
let dragOffset = { x: 0, y: 0 };

const DRAG_THRESHOLD = 5;

const positionStyle = computed(() => {
  if (!position.value) return {};
  return {
    right: `${position.value.right}px`,
    bottom: `${position.value.bottom}px`,
    left: "auto",
    top: "auto",
    transform: "none",
  };
});

function clampToViewport() {
  const el = rootRef.value;
  if (!el || !position.value) return;

  const maxRight = window.innerWidth - el.offsetWidth;
  const maxBottom = window.innerHeight - el.offsetHeight;

  position.value = {
    right: Math.max(0, Math.min(position.value.right, maxRight)),
    bottom: Math.max(0, Math.min(position.value.bottom, maxBottom)),
  };
}

function collapse() {
  guidanceStore.isFloatingExpanded = false;

  nextTick(() => {
    clampToViewport();
  });
}

function onClick() {
  if (hasDragged) return;

  const el = rootRef.value;
  if (!el) return;

  if (!position.value) {
    const rect = el.getBoundingClientRect();
    position.value = {
      right: window.innerWidth - rect.right,
      bottom: window.innerHeight - rect.bottom,
    };
  }

  guidanceStore.isFloatingExpanded = true;

  nextTick(() => {
    clampToViewport();
  });
}

function onDragStart(e: PointerEvent) {
  const el = rootRef.value;
  if (!el) return;

  const target = e.target as HTMLElement;
  if (
    target.closest(".guidance-floating-button__action-btn") ||
    target.closest(".guidance-floating-button__close") ||
    target.closest(".guidance-floating-button__stop-button")
  ) {
    return;
  }

  const rect = el.getBoundingClientRect();

  if (!position.value) {
    position.value = {
      right: window.innerWidth - rect.right,
      bottom: window.innerHeight - rect.bottom,
    };
  }

  dragging = true;
  hasDragged = false;
  startX = e.clientX;
  startY = e.clientY;
  dragOffset.x = rect.right - e.clientX;
  dragOffset.y = rect.bottom - e.clientY;

  window.addEventListener("pointermove", onDragMove);
  window.addEventListener("pointerup", onDragEnd);
}

function onDragMove(e: PointerEvent) {
  if (!dragging || !position.value) return;

  const dx = e.clientX - startX;
  const dy = e.clientY - startY;

  if (!hasDragged && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;

  hasDragged = true;

  const el = rootRef.value;
  if (!el) return;

  const newRight = window.innerWidth - e.clientX - dragOffset.x;
  const newBottom = window.innerHeight - e.clientY - dragOffset.y;

  const maxRight = window.innerWidth - el.offsetWidth;
  const maxBottom = window.innerHeight - el.offsetHeight;

  position.value = {
    right: Math.max(0, Math.min(newRight, maxRight)),
    bottom: Math.max(0, Math.min(newBottom, maxBottom)),
  };
}

function onDragEnd() {
  dragging = false;
  window.removeEventListener("pointermove", onDragMove);
  window.removeEventListener("pointerup", onDragEnd);

  if (hasDragged && position.value) {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position.value));
  }
}

onMounted(() => {
  try {
    const saved = localStorage.getItem(POSITION_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (typeof parsed.right === "number" && typeof parsed.bottom === "number") {
        position.value = parsed;
      }
    }
  } catch {
    console.error("Failed to parse guidance floating position from localStorage");
  }
});

onUnmounted(() => {
  window.removeEventListener("pointermove", onDragMove);
  window.removeEventListener("pointerup", onDragEnd);
});

watch(
  () => guidanceStore.isComplete,
  (complete) => {
    if (complete) {
      localStorage.removeItem(POSITION_STORAGE_KEY);
    }
  },
);

</script>
